/**
 * The WebGL half of the tiled upload. A band goes up as one `texSubImage2D` over its
 * own rows of the texture's bytes, into the storage three allocated. three's
 * `copyTextureToTexture` would read the unpack state back first, and each read waits
 * for every command already queued: 132–174 ms under SwiftShader behind a new 4096²
 * level and the frames drawn meanwhile. It also hands over the whole array for every band.
 */
import { RGBA_BYTES } from '../../resources/processing/mipChain';
import { levelImage } from './TiledUploadQueue';
export function webglUploadRenderer(renderer) {
    // three has required WebGL2 since r163.
    const gl = renderer.getContext();
    /** Binds `texture`'s storage on unit 0 through three's state, so three's own binding cache stays true. */
    const bind = (texture) => {
        const { __webglTexture: storage } = renderer.properties.get(texture);
        renderer.state.bindTexture(gl.TEXTURE_2D, storage, gl.TEXTURE0);
    };
    return {
        initTexture: (texture) => renderer.initTexture(texture),
        writeRows: (texture, { level, fromRow, toRow }) => {
            const { data, width, height } = levelImage(texture, level);
            bind(texture);
            // Through three's cache: its writes skip an unchanged value and its reads stay local.
            const { state } = renderer;
            state.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, texture.flipY);
            state.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, texture.premultiplyAlpha);
            state.pixelStorei(gl.UNPACK_ALIGNMENT, texture.unpackAlignment);
            // The rows are their own view, so no window into a larger image applies.
            for (const windowParameter of [
                gl.UNPACK_ROW_LENGTH,
                gl.UNPACK_IMAGE_HEIGHT,
                gl.UNPACK_SKIP_PIXELS,
                gl.UNPACK_SKIP_ROWS,
                gl.UNPACK_SKIP_IMAGES,
            ]) {
                state.pixelStorei(windowParameter, 0);
            }
            const rowBytes = width * RGBA_BYTES;
            // `UNPACK_FLIP_Y_WEBGL` flips a typed-array band within its own rectangle only, so a flipped
            // texture's band goes to the mirrored rows, where the whole-level upload would put it.
            gl.texSubImage2D(gl.TEXTURE_2D, level, 0, texture.flipY ? height - toRow : fromRow, width, toRow - fromRow, gl.RGBA, gl.UNSIGNED_BYTE, data.subarray(fromRow * rowBytes, toRow * rowBytes));
        },
        generateMipmaps: (texture) => {
            bind(texture);
            gl.generateMipmap(gl.TEXTURE_2D);
        },
    };
}
//# sourceMappingURL=webglUploadRenderer.js.map